import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { Flex } from "@redotech/redo-web/flex";
import LeftArrow from "@redotech/redo-web/icon-old/left-arrow.svg";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo } from "react";
import { useDocumentTitle } from "usehooks-ts";
import { SettingsProvider } from "../contexts/settings";
import { getHeaderText } from "./dynamic-text-utils";
import { EmailLink } from "./email-link";
import { Gift } from "./gift";
import { LandingPage } from "./landing-page";
import * as loginStyles from "./login.module.css";
import * as page from "./page.module.css";
import { PortalDisabled } from "./portal-disabled";
import { Register } from "./register";
import { RegisterOtp } from "./register-otp";
import { LoginPage, useLogin } from "./use-login";

export const Login = memo(function Login() {
  const {
    settings,
    isPortalDisabled,
    currentPage,
    purchaseChannel,
    channelName,
    giftButtonClicked,
    registerButtonClicked,
    emailButtonClicked,
    toOnlineRegistration,
    toRetailRegistration,
    onBackGiftClicked,
    onBackZipcodeClicked,
    onBackRegisterClicked,
    onBackRegisterOtpClicked,
    isMobile,
    errorMessage,
    isCreateGiftReturnLink,
  } = useLogin();

  useDocumentTitle(getHeaderText(settings, isCreateGiftReturnLink));

  return (
    <Flex
      align="center"
      className={page.page}
      flex={1}
      flexDirection="column"
      id="page"
      justify="center"
      p="xxs"
      position="relative"
      style={{
        backgroundImage: `url('${settings?.theme.background_image}')`,
        backgroundPosition: "center",
        backgroundColor: "#F8F8F8",
        maxHeight: "100vh",
      }}
    >
      {isPortalDisabled ? (
        <PortalDisabled color={settings?.theme.home_text_color || "#fff"} />
      ) : (
        <Flex
          align="stretch"
          bgColor="primary"
          className={loginStyles.card}
          flexDirection="column"
          gap="3xl"
          justify="flex-start"
          m="sm"
          maxW={isMobile ? "full" : "none"}
          minW={isMobile ? "none" : "sm"}
          mt="none"
          p="4xl"
          radius="md"
          wrap="nowrap"
        >
          {currentPage === LoginPage.GIFT && (
            <Gift onBack={onBackGiftClicked} />
          )}
          {currentPage === LoginPage.LANDING_PAGE && (
            <LandingPage
              emailButtonClicked={emailButtonClicked}
              giftButtonClicked={giftButtonClicked}
              registerButtonClicked={registerButtonClicked}
              showFooter
              showLogo
            />
          )}
          {currentPage === LoginPage.EMAIL_LINK && (
            <EmailLink onBack={onBackZipcodeClicked} />
          )}
          {currentPage === LoginPage.REGISTER && (
            <Register
              onBack={onBackRegisterClicked}
              toOnlineRegistration={toOnlineRegistration}
              toRetailRegistration={toRetailRegistration}
            />
          )}
          {currentPage === LoginPage.REGISTER_OTP && (
            <RegisterOtp
              channelName={channelName}
              onBack={onBackRegisterOtpClicked}
              purchaseChannel={purchaseChannel}
              redirectRegistrationToRetailActions={
                settings?.warranties?.redirectRegistrationToRetailActions
              }
            />
          )}
          {errorMessage && (
            <Text textAlign="center" textColor="error">
              {errorMessage}
            </Text>
          )}
        </Flex>
      )}

      <StoreReturn className={page.storeReturnBlock} settings={settings} />
    </Flex>
  );
});

const StoreReturn = memo(function StoreReturn({
  className = "",
  settings,
}: {
  className?: string;
  settings?: CustomerPortalAppSettings;
}) {
  const customerStoreUrl =
    settings?.brandKit?.customerFacingDomain || settings?.storeUrl;

  return (
    <a
      className={classNames(className, page.storeReturn)}
      href={customerStoreUrl ? "https://" + customerStoreUrl : ""}
      style={{ color: settings?.theme.home_text_color || "#fff" }}
    >
      {/*todo change the color of this conditionally*/}
      <LeftArrow />
      <div>Back to store</div>
    </a>
  );
});

// For merchant dashboard preview
export const LoginDisplay = memo(function LoginDisplay({
  widgetId,
  overrides,
}: {
  widgetId: string;
  overrides: Partial<CustomerPortalAppSettings>;
}) {
  return (
    <SettingsProvider overrides={overrides} widgetId={widgetId}>
      <Login />
    </SettingsProvider>
  );
});
